import { useEffect } from 'react';
import { useStore } from './store/useStore';
import { I18nProvider } from './i18n';
import Library from './components/Library/Library';
import Reader from './components/Reader/Reader';

function App() {
  const { currentView, readerSettings, books, setCurrentBook, setCurrentView } = useStore();

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark', 'sepia');
    root.classList.add(readerSettings.theme);

    // Brightness filter moved to .app wrapper div instead of body
    // body { filter } breaks position:fixed containing block (CSS spec)
    // causing bottom navbars to anchor to body bottom instead of viewport
    document.body.style.filter = 'none';
  }, [readerSettings.theme, readerSettings.brightness]);

  // ✅ P0-5: /read/:bookId deep link via hash route
  useEffect(() => {
    const handleRoute = () => {
      const hash = window.location.hash;
      // Support #read/bookId format
      const readMatch = hash.match(/^#read\/(.+?)(?:\?|$)/);
      if (readMatch) {
        const bookId = decodeURIComponent(readMatch[1]);
        const found = books.find(b => b.id === bookId);
        if (found) {
          setCurrentBook(found);
          setCurrentView('reader');
          // Update document title for Open Graph / tab title
          document.title = `${found.title} — WIA Book`;
        }
      }
    };

    handleRoute(); // Check on mount
    window.addEventListener('hashchange', handleRoute);
    return () => window.removeEventListener('hashchange', handleRoute);
  }, [books, setCurrentBook, setCurrentView]);

  // ✅ Update hash when entering/leaving reader
  useEffect(() => {
    const { currentBook } = useStore.getState();
    if (currentView === 'reader' && currentBook) {
      const currentHash = window.location.hash;
      // Don't overwrite if already has #page= (FlipbookView manages that)
      if (!currentHash.startsWith('#read/') && !currentHash.startsWith('#page=')) {
        window.history.replaceState(null, '', `#read/${encodeURIComponent(currentBook.id)}`);
      }
      document.title = `${currentBook.title} — WIA Book`;
    } else if (currentView === 'library') {
      if (window.location.hash.startsWith('#read/')) {
        window.history.replaceState(null, '', window.location.pathname);
      }
      document.title = 'WIA Book — Universal Book Reader';
    }
  }, [currentView]);

  return (
    <I18nProvider defaultLanguage="en">
      <div
        className="app min-h-screen"
        style={{ filter: `brightness(${readerSettings.brightness}%)` }}
      >
        {currentView === 'library' ? <Library /> : <Reader />}
      </div>
    </I18nProvider>
  );
}

export default App;
